/**
 * Dados sensíveis que devem ser removidos dos logs
 * Campos específicos relacionados a cartão de crédito
 */
const SENSITIVE_FIELDS = [
  'card_number',
  'cardNumber',
  'cvv',
  'card_cvv',
  'cardCvv',
  'cvv2',
  'security_code',
  'securityCode',
  'holder_name',
  'card_holder_name',
  'cardHolderName',
  'expiration_date',
  'expirationDate',
  'exp_month',
  'exp_year',
];

// Chaves pai que indicam contexto de cartão — quando um objeto está aninhado
// diretamente dentro de uma delas, chaves "bare" (sem prefixo) como `number`
// são tratadas como PAN. Fora desse contexto, `number` permanece visível
// (necessário para logs de endereço/rua, customer.document.number, etc).
const CARD_PARENT_KEYS = new Set(['card', 'cards']);
const CARD_CONTEXT_ONLY_FIELDS = new Set(['number']);

function isSensitiveField(key) {
  const lowerKey = key.toLowerCase();

  if (SENSITIVE_FIELDS.some((field) => lowerKey === field.toLowerCase())) {
    return true;
  }

  // Apenas campos que começam com "card_" (com underscore) são considerados sensíveis
  // Evita falsos positivos como "scorecard", "cardholder" (sem underscore), etc.
  if (lowerKey.startsWith('card_')) {
    return true;
  }

  return false;
}

/**
 * Remove dados sensíveis de cartão do objeto antes de logar.
 * Inclui detecção de referências circulares para evitar stack overflow.
 *
 * `parentKey` carrega a chave imediatamente acima na árvore para permitir
 * sanitização contextual (ex.: mascarar `number` apenas quando dentro de `card`/`cards`).
 */
export function sanitizeForLog(data, visited = new WeakSet(), parentKey) {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (visited.has(data)) {
    return '[CIRCULAR]';
  }

  visited.add(data);
  try {
    if (Array.isArray(data)) {
      return data.map((item) => sanitizeForLog(item, visited, parentKey));
    }

    const inCardContext =
      parentKey != null && CARD_PARENT_KEYS.has(String(parentKey).toLowerCase());

    // Object.create(null) elimina o vetor de prototype pollution mesmo
    // que `Object.keys()` já filtre chaves enumeráveis próprias.
    const sanitized = Object.create(null);

    for (const key of Object.keys(data)) {
      const lowerKey = key.toLowerCase();

      if (isSensitiveField(key)) {
        sanitized[key] = '[REDACTED]';
      } else if (inCardContext && CARD_CONTEXT_ONLY_FIELDS.has(lowerKey)) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof data[key] === 'object' && data[key] !== null) {
        sanitized[key] = sanitizeForLog(data[key], visited, key);
      } else {
        sanitized[key] = data[key];
      }
    }

    return sanitized;
  } finally {
    // Remove do tracking após processar para permitir referências
    // compartilhadas entre irmãos (ciclos reais ainda são detectados
    // pelo `visited.has(data)` no início da função).
    visited.delete(data);
  }
}

export default sanitizeForLog;
